import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Redirect, useHistory } from 'react-router-dom';
import { BrowserView, MobileView, isMobile } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faCrosshairs,
  faInfoCircle,
  faBus,
  faRoute,
  faTimes,
  faBuilding,
  faStore,
  faCoins,
} from '@fortawesome/free-solid-svg-icons';
import { connect as reduxConnect } from 'react-redux';
import { actions } from '#redux/reducers';
import LayoutOnWidth from '#components/HOCs/layoutOnWidth/layoutOnWidth';
import MainMap from '#components/maps/mainMap/mainMap';
import MapCardHeader from '#components/navbars/mapCardHeader/mapCardHeader';
import MapSidebar from '#components/navbars/mapSidebar/mapSidebar';
import MapRoutesSidebar from '#components/navbars/mapRoutesSidebar/mapRoutesSidebar';
import InfoRouteModal from '#components/modals/infoRoute/infoRoute';
import useModal from '#hooks/useModal';
import InfoBusModal from '#components/modals/infoBus/infoBus';
import TermsModal from '#components/modals/terms/terms';
import CrearViajeModal from '#components/modals/viaje/crearViaje/crearViaje';
import { isIFrame } from '#utils/utils';
import { roAPI } from '#utils/axiosAPI';
import { getToken } from '#utils/authService';
import AlertsHOC from '#components/HOCs/alertsHOC/alertsHOC';
import MapButtons from '#components/maps/extras/mapButtons/mapButtons';
import styles from './mapView.module.css';
import InfoStopModal from '#components/modals/infoStop/infoStop';
import InfoPOIModal from '#components/modals/infoPOI/infoPOI';
import LoadingBar from '#components/loading/loadingBar/loadingBar';
import InfoRPModal from '#components/modals/infoRP/infoRP';
import queryString from 'query-string';

const defaultRoute = {
  route_pois: [],
  route_configurations: [],
  route_unions: [],
};

const MapView = ({ terms, acceptTerms, city, cities, routes, trip, updateRoutes, clearTrip, loading, changeCity }) => {
  const {
    location: { search, pathname },
    replace,
    push,
  } = useHistory();

  const mapRef = useRef(null);

  const [showSidebar, setShowSidebar] = React.useState(false);
  const toggleSidebar = () => setShowSidebar(!showSidebar);

  const [showRoutesSidebar, setShowRoutesSidebar] = React.useState(false);
  const toggleRoutesSidebar = () => setShowRoutesSidebar(prevState => !prevState);

  const [showPOIs, setShowPOIs] = React.useState(true);
  const toggleShowPOIs = () => setShowPOIs(prevState => !prevState);

  const [showStops, setShowStops] = React.useState(true);
  const toggleShowStops = () => setShowStops(prevState => !prevState);

  const [showRPs, setShowRPs] = React.useState(true);
  const toggleShowRPs = () => setShowRPs(prevState => !prevState);

  const [route, setRoute] = React.useState(defaultRoute);
  const [routeTwoWays, setRouteTwoWays] = React.useState(false);
  const [busses, setBusses] = React.useState([]);
  const [bus, setBus] = React.useState({});
  const [stops, setStops] = React.useState([]);
  const [stop, setStop] = React.useState({});
  const [poi, setPoi] = React.useState({});
  const [rps, setRPs] = React.useState([]);
  const [rp, setRP] = React.useState({});

  const [showTerms, toggleTerms] = useModal(!terms);
  const [showInfoRoute, toggleInfoRoute] = useModal();
  const [showInfoBus, toggleInfoBus] = useModal();
  const [showInfoStop, toggleInfoStop] = useModal();
  const [showInfoPOI, toggleInfoPOI] = useModal();
  const [showRouteCreate, toggleRouteCreate] = useModal();
  const [showInfoRP, toggleInfoRP] = useModal();

  const handleBusClose = () => {
    toggleInfoBus();
    // setBus({});
  };
  const handleStopClose = () => {
    toggleInfoStop();
    setStop({});
  };
  const handlePOIClose = () => {
    toggleInfoPOI();
    setPoi({});
  };
  const handleRPClose = () => {
    toggleInfoRP();
    setRP({});
  };

  const urlHasBounds = React.useMemo(() => {
    const params = queryString.parse(search);
    return !!(params.ne_lat && params.ne_lng && params.sw_lat && params.sw_lng);
  }, [search]);

  React.useEffect(() => {
    setRoute(defaultRoute);

    const params = queryString.parse(search);
    // Remove bounds params so the map doesn't fit to them
    delete params.ne_lat;
    delete params.ne_lng;
    delete params.sw_lat;
    delete params.sw_lng;
    delete params.ruta;

    push({
      pathname: pathname,
      search: queryString.stringify(params),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  React.useEffect(() => {
    async function fetchStops() {
      if (!route) {
        setStops([]);
        return;
      }
      loading.set();

      try {
        const res = await roAPI.get('/stops', {
          params: {
            route: route.id_route,
          },
        });
        // Map new API fields to old structure for compatibility
        const mappedStops = res.map(s => ({
          ...s,
          codigo: s.code,
          linea: route,
          lineas: s.routes,
        }));
        setStops(mappedStops);
      } catch (error) {
        setStops([]);
      } finally {
        loading.stop();
      }
    }
    if (route.group_id) {
      fetchStops();
    }
  }, [city, route, loading]);

  // fetching busses interval
  React.useEffect(() => {
    async function fetchBussesRO() {
      try {
        const res = await roAPI.get(`/locations/${route.group_id}`);
        const twoWays = res.some(x => x.route_direction === 2);

        setBusses(res.filter(unit => !unit.disabled && !unit.hide && !unit.ignore));
        setRouteTwoWays(twoWays);
      } catch (error) {
        // handle
      }
    }

    if (route.group_id) {
      fetchBussesRO();
    } else {
      return;
    }

    const interval = setInterval(() => {
      if (route.group_id) {
        fetchBussesRO();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [route]);

  // fetching routes of the city.
  React.useEffect(() => {
    async function fetchRoutesRO() {
      try {
        loading.set();
        const res = await roAPI.get(`/routes/city/${city}`);
        // type 1 = bigbang | type 2 = conduent
        const filtered = res.filter(x => !x.metadata.hasOwnProperty('active') || x.metadata.active);
        updateRoutes(filtered);
        loading.stop();
      } catch (error) {
        loading.stop();
      }
    }
    async function fetchRPs() {
      try {
        loading.set();
        const res = await roAPI.get('/rps', {
          params: {
            id_city: city,
          },
        });
        setRPs(res);
        loading.stop();
      } catch (error) {
        loading.stop();
      }
    }
    fetchRoutesRO();
    fetchRPs();
  }, [city, updateRoutes, loading]);

  // on Bus selected
  const handleBussClick = (bus, modal = true) => {
    setBus(bus);
    if (modal) {
      toggleInfoBus();
    }
  };

  React.useEffect(() => {
    if (bus.id) {
      const updated = busses.find(b => b.id === bus.id);
      if (updated) {
        setBus(updated);
      }
    }
  }, [busses, bus]);

  // fetching route
  async function onRouteClick(id) {
    // const route = await beaAPI.get(`/rutas/ciudades/${city}/${id}`);
    const route = routes.find(r => r.group_id === id);
    setRoute(route);
    toggleRoutesSidebar();
    clearTrip();

    const params = queryString.parse(search);
    params.ruta = id;

    // Remove bounds params so the map doesn't fit to them
    delete params.ne_lat;
    delete params.ne_lng;
    delete params.sw_lat;
    delete params.sw_lng;

    push({
      pathname: pathname,
      search: queryString.stringify(params),
    });
  }

  function handleRouteClear() {
    setRoute(defaultRoute);
    setBusses([]);
    setBus({});
    toggleRoutesSidebar();
    setStop({});

    const params = queryString.parse(search);
    delete params.ruta;
    delete params.ne_lat;
    delete params.ne_lng;
    delete params.sw_lat;
    delete params.sw_lng;

    push({
      pathname: pathname,
      search: queryString.stringify(params),
    });
  }

  // Location
  const handleLocation = map => {
    if (trip.selectedTrip || route.group_id) {
      map.resetBounds();
    } else if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          map.ref.leafletElement.flyTo([position.coords.latitude, position.coords.longitude], 16);
        },
        () => {
          map.resetBounds();
        },
      );
    } else {
      map.resetBounds();
    }
  };

  // Trip
  const handleOnTrip = () => {
    setRoute(defaultRoute);
    setBusses([]);
  };

  const selectedCity = React.useMemo(() => {
    if (cities.length && city) {
      const c = cities.find(x => x.id_city === city);
      if (c) {
        return c;
      }
    }
    return null;
  }, [cities, city]);

  /** ***************************************** */
  /** Route Configs */
  /** ***************************************** */

  // TODO: Esto tiene que cambiar para considerar las posibles multiples rutas hijas cada una con su config.
  const routeConfigs = React.useMemo(() => {
    const config = {
      parent: route.route_configurations.sort((a, b) => a.order - b.order),
      child: [],
    };
    if (route.route_unions.length) {
      config.child = route.route_unions[0].route_configurations.sort((a, b) => a.order - b.order);
    }
    return config;
  }, [route]);

  /** ***************************************** */
  /** Compartir lineas */
  /** ***************************************** */

  React.useEffect(() => {
    if (search && cities.length) {
      const params = queryString.parse(search);
      changeCity(params.ciudad);
    }
  }, [search, changeCity, cities]);

  React.useEffect(() => {
    if (search && routes.length) {
      const params = queryString.parse(search);

      if (params.ruta) {
        const route = routes.find(r => r.group_id === params.ruta);
        setRoute(route);
      }

      // Fit to bounds if all bounds params are present
      if (params.ne_lat && params.ne_lng && params.sw_lat && params.sw_lng) {
        const bounds = [
          [parseFloat(params.sw_lat), parseFloat(params.sw_lng)],
          [parseFloat(params.ne_lat), parseFloat(params.ne_lng)],
        ];
        // setTimeout(() => {
        mapRef.current.leafletElement.fitBounds(bounds);
        // }, 500);
      }
    }
  }, [search, mapRef, routes]);

  React.useEffect(() => {
    if (search && routes.length && busses.length) {
      const params = queryString.parse(search);
      const camion = busses.find(b => b.id === params.camion);
      if (camion) {
        setBus(camion);
      }
      // toggleInfoBus();
    }
  }, [search, busses, routes, replace]);

  const handleBoundsChange = useCallback(
    ({ ne, sw }) => {
      const params = queryString.parse(search);
      params.ne_lat = ne.lat.toFixed(6);
      params.ne_lng = ne.lng.toFixed(6);
      params.sw_lat = sw.lat.toFixed(6);
      params.sw_lng = sw.lng.toFixed(6);
      replace({
        pathname,
        search: queryString.stringify(params),
      });
    },
    [search, replace, pathname],
  );

  // TODO:
  // Logica de camión seleccionado: Seguir automaticamente al camión.
  // Icono de color azul al estar seleccionado.

  /** ***************************************** */
  /** Render */
  /** ***************************************** */
  const mainMap = (
    <MainMap // This is your custom component, not the library one.
      ref={mapRef}
      city={city}
      cities={cities}
      route={route}
      bus={bus}
      busses={busses}
      onBusClick={handleBussClick}
      tripRoute={trip.routes[trip.selectedTrip]}
      showPOIs={showPOIs}
      stops={stops}
      showStops={showStops}
      onStopClick={stop => {
        setStop(stop);
        toggleInfoStop();
      }}
      onPOIClick={poi => {
        setPoi(poi);
        toggleInfoPOI();
      }}
      rps={rps}
      showRPs={showRPs}
      onRPClick={rp => {
        setRP(rp);
        toggleInfoRP();
      }}
      routeConfigs={routeConfigs}
      onBoundsChange={handleBoundsChange}
      urlHasBounds={urlHasBounds}
    >
      <MapButtons
        disableButtons={showSidebar || showRoutesSidebar}
        buttons={[
          {
            label: <FontAwesomeIcon icon={faCrosshairs} />,
            class: 'btn btn-light mirai-shadow',
            onClick: handleLocation,
          },
          ...(route.group_id
            ? [
                {
                  label: <FontAwesomeIcon icon={faBuilding} />,
                  class: `btn ${showPOIs ? 'btn-primary' : 'btn-secondary'} mirai-shadow`,
                  title: 'Mostrar puntos de interes',
                  onClick: toggleShowPOIs,
                },
              ]
            : []),
          ...(route.group_id
            ? [
                {
                  label: <FontAwesomeIcon icon={faStore} />,
                  class: `btn ${showStops ? 'btn-primary' : 'btn-secondary'} mirai-shadow`,
                  title: 'Mostrar paradas de autobús',
                  onClick: toggleShowStops,
                },
              ]
            : []),
          {
            label: <FontAwesomeIcon icon={faCoins} />,
            class: `btn ${showRPs ? 'btn-primary' : 'btn-secondary'} mirai-shadow`,
            title: 'Mostrar centros de recarga',
            onClick: toggleShowRPs,
          },
          ...(route.group_id
            ? [
                {
                  label: <FontAwesomeIcon icon={faInfoCircle} />,
                  class: 'btn btn-info mirai-shadow',
                  onClick: toggleInfoRoute,
                },
              ]
            : []),
          {
            label: (
              <React.Fragment>
                <FontAwesomeIcon icon={faBus} style={{ marginRight: '0.5rem' }} />
                {route.group_id ? <span>{route.name}</span> : <span>Ver una línea</span>}
              </React.Fragment>
            ),
            class: `btn btn-une mirai-shadow btn-selectroute ${route.group_id && 'btn-routeselected'}`,
            onClick: toggleRoutesSidebar,
            fullWidth: isMobile,
          },
          ...(getToken() && selectedCity && selectedCity.routing_enabled
            ? [
                {
                  label: (
                    <React.Fragment>
                      <FontAwesomeIcon icon={faRoute} style={{ marginRight: '0.5rem' }} />
                      {trip.selectedTrip ? 'Instrucciones de viaje' : 'Crear viaje'}
                    </React.Fragment>
                  ),
                  class: 'btn btn-light mirai-shadow btn-selectroute',
                  onClick: toggleRouteCreate,
                  fullWidth: isMobile,
                },
              ]
            : []),
          ...(getToken() && selectedCity && selectedCity.routing_enabled && trip.selectedTrip && !isMobile
            ? [
                {
                  label: <FontAwesomeIcon icon={faTimes} />,
                  class: 'btn btn-light mirai-shadow',
                  onClick: clearTrip,
                },
              ]
            : []),
        ]}
      />
    </MainMap>
  );

  const mapLeftSide = [
    <div key="mapsidebar" className={styles['left']}>
      <MapSidebar show={showSidebar} close={toggleSidebar} />
    </div>,
    <div key="maproutessidebar" className={styles['left']}>
      <MapRoutesSidebar
        show={showRoutesSidebar}
        close={toggleRoutesSidebar}
        routes={routes}
        onRouteClick={onRouteClick}
        activeRoute={route.group_id}
        clearRoute={handleRouteClear}
      />
    </div>,
  ];

  if (!city) {
    return <Redirect to="/" />;
  }

  return (
    <React.Fragment>
      <BrowserView>
        <div data-testid="MapView" className={`card mirai-shadow ${styles['browser-card']} ${isIFrame() && styles['iframe']}`}>
          <div className={`card-body mirai-shadow ${styles['browser-card-header']}`}>
            <MapCardHeader
              onBtnClick={toggleSidebar}
              disableButtons={showSidebar || showRoutesSidebar}
              title={selectedCity ? selectedCity.name : 'Ciudad'}
              subtitle={route.name ? route.name : 'Seleccione línea'}
            />
          </div>
          <div className={styles['card-map']}>
            <LoadingBar show color="#FF6A29" />
            {mapLeftSide}
            <div className={styles['right']}>{mainMap}</div>
          </div>
        </div>
      </BrowserView>
      <MobileView>
        <div data-testid="MapView">
          <div className={styles['card-map-mobile']}>
            {mapLeftSide}
            <div className={styles['right']}>
              <button type="button" className={`btn btn-light mirai-shadow ${styles['floating-button']}`} onClick={toggleSidebar}>
                <FontAwesomeIcon icon={faBars} />
              </button>
              {mainMap}
            </div>
          </div>
        </div>
      </MobileView>
      {/** Modales. */}
      <InfoRouteModal showModal={showInfoRoute} toggleModal={toggleInfoRoute} route={route} />
      <InfoBusModal
        showModal={showInfoBus}
        toggleModal={handleBusClose}
        bus={bus}
        routeTwoWays={routeTwoWays}
        routeConfigs={routeConfigs}
      />
      <TermsModal showModal={showTerms} toggleModal={toggleTerms} onAccept={acceptTerms} />
      <CrearViajeModal showModal={showRouteCreate} toggleModal={toggleRouteCreate} onTrip={handleOnTrip} />
      <InfoStopModal showModal={showInfoStop} toggleModal={handleStopClose} stop={stop} route={route} />
      <InfoPOIModal showModal={showInfoPOI} toggleModal={handlePOIClose} poi={poi} />
      <InfoRPModal showModal={showInfoRP} toggleModal={handleRPClose} rp={rp} />
    </React.Fragment>
  );
};

MapView.propTypes = {
  terms: PropTypes.bool,
  acceptTerms: PropTypes.func,
  city: PropTypes.string,
  cities: PropTypes.array,
  routes: PropTypes.array,
  trip: PropTypes.shape({
    origin: PropTypes.any,
    destination: PropTypes.any,
    routes: PropTypes.array,
    selectedTrip: PropTypes.number,
  }),
  updateRoutes: PropTypes.func,
  clearTrip: PropTypes.func,
  loading: PropTypes.shape({
    set: PropTypes.func,
    stop: PropTypes.func,
  }),
  changeCity: PropTypes.func,
};

MapView.defaultProps = {
  terms: false,
  acceptTerms: f => f,
  city: '1',
  cities: [],
  routes: [],
  trip: {
    origin: null,
    destination: null,
    routes: [],
    selectedTrip: 0,
  },
  updateRoutes: f => f,
  clearTrip: f => f,
  loading: {
    set: f => f,
    stop: f => f,
  },
  changeCity: f => f,
};

const ConnectedMapView = reduxConnect(
  state => ({
    terms: state.terms,
    city: state.city,
    cities: state.cities,
    routes: state.routes,
    trip: state.trip,
  }),
  dispatch => ({
    updateRoutes: routes => dispatch(actions.updateRoutes(routes)),
    acceptTerms: () => dispatch(actions.acceptTerms(true)),
    clearTrip: () => dispatch(actions.tripClearTrip()),
    loading: {
      set: () => dispatch(actions.loadingSet()),
      stop: () => dispatch(actions.loadingStop()),
    },
    changeCity: city => dispatch(actions.changeCity(city)),
  }),
)(MapView);

const mapLayout = LayoutOnWidth(ConnectedMapView, {
  browser: {
    backgroundStyle: {
      backgroundColor: '#f5f5f5',
      height: '100vh',
    },
    containerStyle: {
      height: '100%',
      alignItems: 'center',
    },
    rowStyle: {
      height: '100%',
      alignItems: 'center',
    },
    rowClass: 'row justify-content-center',
    colClass: 'col-12',
  },
});

export default AlertsHOC(mapLayout);
